import { useTranslation } from 'react-i18next';
import { Palette } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import {
  AVATAR_COLORS,
  avatarFaceClass,
  avatarSwatchClass,
  initialsOf,
  type AvatarColor,
} from '../../lib/avatar-colors';

interface AthleteAvatarProps {
  name?: string | null;
  surname?: string | null;
  /** The colour chosen for them; a hue derived from the name when there is none. */
  color?: string | null;
  /** A picture, for the one account in the picker that can have one. */
  imageUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  /** Given, the avatar becomes the way the colour is changed. */
  onColorChange?: (color: AvatarColor) => void;
  className?: string;
}

const SIZES = {
  sm: 'size-10 text-sm',
  md: 'size-14 text-lg',
  lg: 'size-20 text-2xl',
};

/**
 * How an athlete is drawn where there is no photograph to draw: a coloured
 * disc with their initials.
 *
 * A child's managed account never has a picture — nothing in the app would
 * show one — so on the picker that asks which of them is being entered, the
 * colour is the thing that tells two siblings apart before the name is read.
 * It is the parent's to choose: tap the avatar, pick from the palette, and the
 * choice is saved on the account (`users.avatar_color`).
 */
export default function AthleteAvatar({
  name,
  surname,
  color,
  imageUrl = null,
  size = 'md',
  onColorChange,
  className,
}: AthleteAvatarProps) {
  const { t } = useTranslation();
  const initials = initialsOf(name, surname);
  const seed = `${name ?? ''} ${surname ?? ''}`.trim();

  const face = (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold',
        SIZES[size],
        avatarFaceClass(color, seed),
        className
      )}
      data-testid="athlete-avatar"
      data-color={color ?? 'auto'}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        initials
      )}
    </span>
  );

  if (!onColorChange) return face;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={t('guardian.picker.changeColor')}
          data-testid="change-avatar-color"
        >
          {face}
          {/* A hint that the disc is a control; small enough not to compete
              with the initials it sits on. */}
          <span className="absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full border border-background bg-muted text-muted-foreground">
            <Palette className="size-3" aria-hidden />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="center">
        <div className="grid grid-cols-6 gap-1.5">
          {AVATAR_COLORS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onColorChange(option)}
              aria-label={option}
              data-testid={`avatar-color-${option}`}
              className={cn(
                'size-6 rounded-full ring-offset-2 ring-offset-background transition hover:scale-110',
                avatarSwatchClass(option),
                color === option && 'ring-2 ring-foreground'
              )}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
